import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Button, Form, Input, Select, Switch, Table, Tag, Badge,
    Alert, Spin, message, Modal, Divider, Row, Col, Statistic,
    Tooltip, Steps, Space,
} from 'antd';
import {
    ApiOutlined, SyncOutlined, EyeOutlined, CheckCircleOutlined,
    WarningOutlined, PlusCircleOutlined, EditOutlined, ClockCircleOutlined,
    SaveOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import axios from '../../utils/axios';
import { trailHieSync, trailUpdate, trailPageView, logTrail, TRAIL_EVENTS, TRAIL_MODULES } from '../../utils/activityTrail';
const { Option } = Select;
const { Step } = Steps;

export default function QoCHIESync() {
    const [config,      setConfig]      = useState({});
    const [configForm]                  = Form.useForm();
    const [savingCfg,   setSavingCfg]   = useState(false);

    const [testing,     setTesting]     = useState(false);
    const [testResult,  setTestResult]  = useState(null); // {ok, message, fhir_version}

    const [previewing,  setPreviewing]  = useState(false);
    const [previewData, setPreviewData] = useState(null); // diff result

    const [syncing,     setSyncing]     = useState(false);
    const [syncResult,  setSyncResult]  = useState(null);

    const [logs,        setLogs]        = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);

    const [confirmModal, setConfirmModal] = useState(false);

    // ── Load config on mount ─────────────────────────────────────────────────
    const loadConfig = useCallback(async () => {
        try {
            const res = await axios.get('/qoc/hie/config');
            const d   = res.data?.data || {};
            setConfig(d);
            configForm.setFieldsValue({
                fhir_base_url: d.fhir_base_url || '',
                auth_type:     d.auth_type      || 'bearer',
                auth_header:   d.auth_header    || 'Authorization',
                sync_mode:     d.sync_mode      || 'upsert',
                auth_token:    '',
            });
        } catch (e) { /* config not set yet — ok */ }
    }, [configForm]);

    const loadLogs = useCallback(async () => {
        setLogsLoading(true);
        try {
            const res = await axios.get('/qoc/hie/logs');
            setLogs(res.data?.data || []);
        } catch (e) {}
        setLogsLoading(false);
    }, []);

    useEffect(() => {
        trailPageView(TRAIL_MODULES.HIE_SYNC, window.location.pathname);
        loadConfig();
        loadLogs();
    }, [loadConfig, loadLogs]);
    // ── Save config ──────────────────────────────────────────────────────────
    const saveConfig = async () => {
        try {
            const vals = await configForm.validateFields();
            setSavingCfg(true);
            await axios.post('/qoc/hie/config', vals);

            trailUpdate(TRAIL_MODULES.HIE_SYNC, 'HIE Config', '', { authType: vals.auth_type, syncMode: vals.sync_mode });
            message.success('Configuration saved');
            setTestResult(null);
            setPreviewData(null);
            loadConfig();
        } catch (e) {
            if (e?.errorFields) return;
            message.error('Save failed');
        }
        setSavingCfg(false);
    };

    // ── Test connection ───────────────────────────────────────────────────────
    const testConn = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const res = await axios.get('/qoc/hie/test');
           // REPLACE WITH:
            if (res.data?.code === 'S001' || res.data?.code === 200) {
                setTestResult({ ok: true, ...res.data.data });
                logTrail(TRAIL_EVENTS.RECORD_VIEW, TRAIL_MODULES.HIE_SYNC,
                    { result: 'success', fhirVersion: res.data.data?.fhir_version }, 'HIE connection test passed');
            } else {
                setTestResult({ ok: false, message: res.data?.message || 'Test failed' });
                logTrail(TRAIL_EVENTS.ERROR, TRAIL_MODULES.HIE_SYNC,
                    { result: 'failed', message: res.data?.message }, 'HIE connection test failed');
            }
        } catch (e) {
            setTestResult({ ok: false, message: e?.response?.data?.message || e.message });
        }
        setTesting(false);
    };

    // ── Preview ───────────────────────────────────────────────────────────────
    const runPreview = async () => {
        setPreviewing(true);
        setPreviewData(null);
        try {
            const res = await axios.get('/qoc/hie/preview');
            setPreviewData(res.data?.data);
        } catch (e) {
            message.error(e?.response?.data?.message || 'Preview failed');
        }
        setPreviewing(false);
    };

    // ── Sync ──────────────────────────────────────────────────────────────────
    const runSync = async () => {
        setConfirmModal(false);
        setSyncing(true);
        setSyncResult(null);
        try {
            const res = await axios.post('/qoc/hie/sync');
            setSyncResult(res.data);

            const syncData = res.data?.data || res.data || {};
            trailHieSync(
                (syncData.total_new || 0) + (syncData.total_updated || 0),
                'success',
                `${syncData.total_new || 0} new, ${syncData.total_updated || 0} updated`
            );
            message.success(res.data?.message || 'Sync complete');
            loadLogs();
            // Clear HIE badge from localStorage
            localStorage.removeItem('hie_badge');
        
        } catch (e) {
            const errMsg = e?.response?.data?.message || 'Sync failed';
            trailHieSync(0, 'failed', errMsg);
            message.error(errMsg);
        }
        setSyncing(false);
    };

    // ── Preview table columns ─────────────────────────────────────────────────
    const ACTION_TAG = {
        new:    <Tag color="green"  icon={<PlusCircleOutlined />}>New</Tag>,
        update: <Tag color="blue"   icon={<EditOutlined />}>Update</Tag>,
        skip:   <Tag color="default">No Change</Tag>,
    };

    const previewColumns = [
        {
            title: 'Facility (HIE)',
            key: 'name',
            render: (_, r) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{r.hie?.name}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>HIE ID: {r.hie?.hie_id}</div>
                </div>
            ),
        },
        { title: 'Action',  key: 'action',  width: 110, render: (_, r) => ACTION_TAG[r.action] || r.action },
        { title: 'Region',  key: 'region',  width: 130, render: (_, r) => r.hie?.region || <span style={{ color: '#f59e0b' }}>⚠ blank</span> },
        { title: 'Type',    key: 'type',    width: 160, render: (_, r) => r.hie?.type   || <span style={{ color: '#f59e0b' }}>⚠ blank</span> },
        {
            title: 'Changes',
            key: 'changes',
            render: (_, r) => r.changes
                ? Object.entries(r.changes).map(([k, v]) => (
                    <div key={k} style={{ fontSize: 11 }}>
                        <span style={{ color: '#94a3b8' }}>{k}:</span> <span style={{ color: '#2563eb' }}>{v}</span>
                    </div>
                ))
                : r.action === 'skip' ? <span style={{ color: '#d1d5db', fontSize: 11 }}>—</span> : null,
        },
        {
            title: 'Blank Fields',
            key: 'blanks',
            width: 120,
            render: (_, r) => r.blank_fields?.length
                ? <Tag color="orange">{r.blank_fields.length} blank</Tag>
                : <CheckCircleOutlined style={{ color: '#16a34a' }} />,
        },
    ];

    // ── Log table columns ─────────────────────────────────────────────────────
    const logColumns = [
        { title: 'Date',     dataIndex: 'created_at', key: 'created_at', width: 160 },
        { title: 'Type',     dataIndex: 'sync_type',  key: 'sync_type',  width: 100, render: t => <Tag>{t}</Tag> },
        {
            title: 'Status', dataIndex: 'status', key: 'status', width: 100,
            render: s => <Tag color={s === 'success' ? 'success' : s === 'partial' ? 'warning' : 'error'}>{s}</Tag>,
        },
        { title: 'Fetched',  dataIndex: 'total_fetched',  key: 'fetched',  width: 80, align: 'center' },
        { title: 'New',      dataIndex: 'total_new',      key: 'new',      width: 70, align: 'center', render: v => <span style={{ color: v > 0 ? '#16a34a' : undefined }}>{v}</span> },
        { title: 'Updated',  dataIndex: 'total_updated',  key: 'updated',  width: 80, align: 'center', render: v => <span style={{ color: v > 0 ? '#2563eb' : undefined }}>{v}</span> },
        { title: 'Skipped',  dataIndex: 'total_skipped',  key: 'skipped',  width: 80, align: 'center' },
        { title: 'Blanks',   dataIndex: 'total_blank',    key: 'blank',    width: 70, align: 'center', render: v => v > 0 ? <Tag color="orange">{v}</Tag> : '—' },
    ];

    const previewCounts = previewData?.counts || {};

    return (
        <div className="page-content">
        <div style={{ padding: 24, maxWidth: 1100 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
                <div style={{
                    width: 44, height: 44, borderRadius: 12,
                    background: 'linear-gradient(135deg,#16a34a,#0ea5e9)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <ApiOutlined style={{ color: '#fff', fontSize: 20 }} />
                </div>
                <div>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>HIE Facility Sync</h2>
                    <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>
                        Sync facilities from the national Health Information Exchange (FHIR HL7 R4)
                    </p>
                </div>
            </div>

            {/* ── Step 1: Configure ─────────────────────────────────────── */}
            <Card
                title={<span><ApiOutlined /> Step 1 — FHIR Connection</span>}
                style={{ marginBottom: 20, borderRadius: 12 }}
            >
                <Form form={configForm} layout="vertical">
                    <Row gutter={16}>
                        <Col xs={24} md={14}>
                            <Form.Item label="FHIR Base URL" name="fhir_base_url"
                                rules={[{ required: true, message: 'FHIR base URL is required' }]}
                                help="e.g. https://hie.health.gov.sz/fhir"
                            >
                                <Input placeholder="https://hie.example.org/fhir" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={10}>
                            <Form.Item label="Auth Type" name="auth_type">
                                <Select>
                                    <Option value="none">None (public)</Option>
                                    <Option value="bearer">Bearer Token</Option>
                                    <Option value="apikey">API Key</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} md={14}>
                            <Form.Item
                                label={config.auth_token_masked ? `Auth Token (current: ${config.auth_token_masked})` : 'Auth Token'}
                                name="auth_token"
                                help="Leave blank to keep existing token"
                            >
                                <Input.Password placeholder="Paste new token to update" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={10}>
                            <Form.Item label="Sync Mode" name="sync_mode">
                                <Select>
                                    <Option value="upsert">Add new + Update changed</Option>
                                    <Option value="create_only">Add new only</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>

                <Space>
                    <Button type="primary" icon={<SaveOutlined />} loading={savingCfg} onClick={saveConfig}
                        style={{ background: '#7c3aed', borderColor: '#7c3aed' }}>
                        Save Configuration
                    </Button>
                    <Button icon={<ThunderboltOutlined />} loading={testing} onClick={testConn}>
                        Test Connection
                    </Button>
                </Space>

                {testResult && (
                    <Alert
                        style={{ marginTop: 16 }}
                        type={testResult.ok ? 'success' : 'error'}
                        showIcon
                        message={testResult.ok
                            ? `Connected — FHIR ${testResult.fhir_version || ''} (${testResult.software || 'unknown'})`
                            : `Connection failed: ${testResult.message}`}
                    />
                )}
            </Card>

            {/* ── Step 2: Preview ───────────────────────────────────────── */}
            <Card
                title={<span><EyeOutlined /> Step 2 — Preview Changes</span>}
                style={{ marginBottom: 20, borderRadius: 12 }}
                extra={
                    <Button icon={<EyeOutlined />} loading={previewing} onClick={runPreview}>
                        Fetch from HIE & Preview
                    </Button>
                }
            >
                {!previewData && !previewing && (
                    <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>
                        Click "Fetch from HIE & Preview" to see what will change before syncing.
                    </div>
                )}
                {previewing && <Spin tip="Fetching from HIE…" style={{ display: 'block', textAlign: 'center', padding: 20 }} />}

                {previewData && (
                    <>
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={6}><Statistic title="Total HIE records"  value={previewData.rows?.length || 0} /></Col>
                            <Col span={6}><Statistic title="New facilities" value={previewCounts.new || 0}     valueStyle={{ color: '#16a34a' }} /></Col>
                            <Col span={6}><Statistic title="To update"      value={previewCounts.updated || 0} valueStyle={{ color: '#2563eb' }} /></Col>
                            <Col span={6}><Statistic title="Blank fields"   value={previewCounts.blank_fields || 0} valueStyle={{ color: previewCounts.blank_fields > 0 ? '#f59e0b' : '#16a34a' }} /></Col>
                        </Row>
                        {previewCounts.blank_fields > 0 && (
                            <Alert
                                type="warning" showIcon style={{ marginBottom: 12 }}
                                message={`${previewCounts.blank_fields} blank fields detected (region, type, etc.). These facilities will still be imported — you can edit them manually in the Facilities module after sync.`}
                            />
                        )}
                        <Table
                            dataSource={previewData.rows}
                            columns={previewColumns}
                            rowKey={(r, i) => r.hie?.hie_id || i}
                            size="small"
                            pagination={{ pageSize: 20 }}
                            scroll={{ x: 800 }}
                        />
                    </>
                )}
            </Card>

            {/* ── Step 3: Sync ──────────────────────────────────────────── */}
            <Card
                title={<span><SyncOutlined /> Step 3 — Apply Sync</span>}
                style={{ marginBottom: 20, borderRadius: 12 }}
            >
                {syncResult && (
                    <Alert
                        type={syncResult.code === 'S001' || syncResult.code === 200 ? 'success' : 'error'}
                        showIcon message={syncResult.message} style={{ marginBottom: 16 }}
                    />
                )}
                <p style={{ color: '#64748b', marginBottom: 16, fontSize: 13 }}>
                    This will apply the changes shown in the preview to the facilities database.
                    Existing facilities are never deleted — only added or updated.
                </p>
                <Button
                    type="primary" size="large" icon={<SyncOutlined />}
                    loading={syncing}
                    disabled={!previewData || (previewCounts.new === 0 && previewCounts.updated === 0)}
                    onClick={() => setConfirmModal(true)}
                    style={{ background: '#16a34a', borderColor: '#16a34a' }}
                >
                    Sync Now {previewData ? `(${(previewCounts.new || 0) + (previewCounts.updated || 0)} changes)` : ''}
                </Button>
            </Card>

            {/* ── Sync Log ──────────────────────────────────────────────── */}
            <Card title={<span><ClockCircleOutlined /> Sync History</span>} style={{ borderRadius: 12 }}
                extra={<Button size="small" onClick={loadLogs}>Refresh</Button>}>
                <Table
                    dataSource={logs} columns={logColumns} rowKey="id"
                    loading={logsLoading} size="small"
                    pagination={{ pageSize: 10 }}
                />
            </Card>

            {/* Confirm Modal */}
            <Modal
                title="Confirm Sync"
                open={confirmModal}
                onOk={runSync}
                onCancel={() => setConfirmModal(false)}
                okText="Yes, Sync Now"
                okButtonProps={{ style: { background: '#16a34a', borderColor: '#16a34a' } }}
            >
                <p>This will apply <strong>{(previewCounts.new || 0) + (previewCounts.updated || 0)}</strong> changes to the facilities database:</p>
                <ul>
                    {previewCounts.new > 0 && <li><strong>{previewCounts.new}</strong> new facilities will be added</li>}
                    {previewCounts.updated > 0 && <li><strong>{previewCounts.updated}</strong> existing facilities will be updated</li>}
                    {previewCounts.blank_fields > 0 && <li><strong>{previewCounts.blank_fields}</strong> blank fields will need manual editing</li>}
                </ul>
                <p>Facilities are never deleted by sync. Continue?</p>
            </Modal>
        </div>
        </div>
    );
}